<template>
    <div class="scan-win-form">
        <!-- Success: non-winner -->
        <div v-if="submitted && !instantPrize" class="success-screen">
            <div class="success-panel">
                <div class="success-confetti" aria-hidden="true">
                    <span v-for="i in 12" :key="i" class="confetti-piece" :style="confettiStyle(i)"></span>
                </div>
                <div class="success-panel-body">
                    <div class="success-icon-wrap">
                        <div class="success-icon">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="success-icon-svg">
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                        </div>
                    </div>
                    <h2 class="success-heading">You're in the draw!</h2>
                    <p v-if="weight > 1" class="success-multiplier">
                        It's a <strong>{{ weight }}x points day</strong> — your entry counts {{ weight }} times in the major draw!
                    </p>
                    <p class="success-note">Keep your receipt — proof of purchase is required to claim any prize.</p>
                    <button type="button" class="submit-btn success-reset-btn" @click="resetForm">
                        ENTER AGAIN
                    </button>
                </div>
            </div>
        </div>

        <!-- Success: instant winner (same chrome as draw confirmation; copy + icon only) -->
        <div v-if="submitted && instantPrize" class="success-screen success-screen--winner">
            <div class="success-panel success-panel--instant-win">
                <div class="success-confetti" aria-hidden="true">
                    <span v-for="i in 12" :key="i" class="confetti-piece" :style="confettiStyle(i)"></span>
                </div>
                <div class="success-fireworks" aria-hidden="true">
                    <div
                        v-for="burst in 3"
                        :key="'fw-' + burst"
                        class="firework-burst"
                        :style="fireworkBurstStyle(burst)"
                    >
                        <span
                            v-for="particle in 10"
                            :key="burst + '-' + particle"
                            class="firework-particle"
                            :style="fireworkParticleStyle(burst, particle)"
                        ></span>
                    </div>
                </div>
                <div class="success-panel-body">
                    <div class="success-icon-wrap">
                        <div class="success-icon success-icon--winner">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round" class="success-icon-svg" aria-hidden="true">
                                <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
                                <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
                                <path d="M4 22h16" />
                                <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
                                <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
                                <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
                            </svg>
                        </div>
                    </div>
                    <h2 class="success-heading">You're an instant winner!</h2>
                    <p class="success-multiplier success-multiplier--prize">
                        Your prize: <strong>{{ instantPrize.name }}</strong>
                    </p>
                    <p class="success-note">You’ve got mail! Check your email inbox now to see how to claim your prize at the North City Customer Service Desk. Show your winner’s email to claim your prize within 7 days. Congratulations!</p>
                    <button type="button" class="submit-btn success-reset-btn" @click="resetForm">
                        ENTER AGAIN
                    </button>
                </div>
            </div>
        </div>

        <!-- Form -->
        <form v-if="!submitted" @submit.prevent="submitForm" novalidate>
            <div class="form-grid">
                <div class="form-field" :class="{ 'has-error': errors.firstName }">
                    <input
                        type="text"
                        v-model="form.firstName"
                        placeholder="First Name"
                    />
                    <span v-if="errors.firstName" class="error-msg">{{ errors.firstName }}</span>
                </div>
                <div class="form-field" :class="{ 'has-error': errors.lastName }">
                    <input
                        type="text"
                        v-model="form.lastName"
                        placeholder="Last Name"
                    />
                    <span v-if="errors.lastName" class="error-msg">{{ errors.lastName }}</span>
                </div>
            </div>

            <div class="form-grid">
                <div class="form-field" :class="{ 'has-error': errors.entrantPhone }">
                    <input
                        type="tel"
                        v-model="form.entrantPhone"
                        placeholder="Phone"
                    />
                    <span v-if="errors.entrantPhone" class="error-msg">{{ errors.entrantPhone }}</span>
                </div>
                <div class="form-field" :class="{ 'has-error': errors.entrantEmail }">
                    <input
                        type="email"
                        v-model="form.entrantEmail"
                        placeholder="Email"
                    />
                    <span v-if="errors.entrantEmail" class="error-msg">{{ errors.entrantEmail }}</span>
                </div>
            </div>

            <div class="form-grid">
                <div class="form-field form-field-select" :class="{ 'has-error': errors.store }">
                    <select v-model="form.store">
                        <option value="">Select store</option>
                        <option v-for="s in stores" :key="s.id" :value="s.id">{{ s.title }}</option>
                    </select>
                    <span v-if="errors.store" class="error-msg">{{ errors.store }}</span>
                </div>
                <div class="form-field" :class="{ 'has-error': errors.spendAmount }">
                    <input
                        type="number"
                        v-model.number="form.spendAmount"
                        placeholder="Amount spent $"
                        min="1"
                    />
                    <span v-if="errors.spendAmount" class="error-msg">{{ errors.spendAmount }}</span>
                </div>
            </div>

            <div class="proof-notice">
                *Proof of purchase is required to be show to redeem prizes. Receipt or bank transaction accepted.
            </div>

            <div class="terms-row" :class="{ 'has-error': errors.terms }">
                <label class="terms-label">
                    <input type="checkbox" v-model="form.terms" />
                    <span>I have read and agree to the <a href="/assets/documents/Lucky-Me-Terms-and-Conditions.pdf" target="_blank">Terms and Conditions</a> for this promotion</span>
                </label>
                <p class="terms-sub">This includes receiving promotional emails from North City Shopping Centre.<br/>You can unsubscribe at any time.</p>
                <span v-if="errors.terms" class="error-msg">{{ errors.terms }}</span>
            </div>

            <span v-if="errors.form" class="error-msg form-error">{{ errors.form }}</span>

            <button type="submit" class="submit-btn" :disabled="submitting">
                <span v-if="submitting">SUBMITTING...</span>
                <span v-else>SUBMIT YOUR DETAILS</span>
            </button>
        </form>
    </div>
</template>

<script>
export default {
    name: 'ScanWinForm',

    props: {
        stores: {
            type: Array,
            required: true,
        },
        csrfName: {
            type: String,
            required: true,
        },
        csrfToken: {
            type: String,
            required: true,
        },
    },

    data() {
        return {
            form: {
                firstName: '',
                lastName: '',
                entrantEmail: '',
                entrantPhone: '',
                spendAmount: '',
                store: '',
                terms: false,
            },
            errors: {},
            submitting: false,
            submitted: false,
            resultMessage: '',
            instantPrize: null,
            weight: 1,
        };
    },

    computed: {
        storeName() {
            if (!this.form.store) return '';
            const s = this.stores.find(s => String(s.id) === String(this.form.store));
            return s ? s.title : '';
        },
    },

    watch: {
        submitted(val) {
            this.syncFormSectionSuccessClass(val);
        },
    },

    mounted() {
        this.syncFormSectionSuccessClass(this.submitted);
    },

    beforeUnmount() {
        this.syncFormSectionSuccessClass(false);
    },

    methods: {
        syncFormSectionSuccessClass(on) {
            const section = document.getElementById('form');
            if (!section) return;
            const active = !!on;
            section.classList.toggle('form-section--success', active);
            section.classList.toggle('form-section--instant-win', active && !!this.instantPrize);
        },

        confettiStyle(i) {
            const colors = ['#F6BE89', '#112E29', '#A8C6C4', '#D29884', '#9C7953', '#48bb78'];
            const color = colors[i % colors.length];
            const left = 10 + (i * 7) % 80;
            const delay = (i * 0.15).toFixed(2);
            const size = 6 + (i % 4) * 2;
            return {
                left: `${left}%`,
                animationDelay: `${delay}s`,
                background: color,
                width: `${size}px`,
                height: `${size}px`,
            };
        },

        fireworkBurstStyle(index) {
            const spots = [
                { top: '16%', left: '50%', transform: 'translateX(-50%)' },
                { top: '42%', left: '10%' },
                { top: '38%', left: 'auto', right: '10%' },
            ];
            return spots[index - 1] || spots[0];
        },

        fireworkParticleStyle(burst, particle) {
            const rays = 10;
            const jitter = ((burst * 19 + particle * 13) % 26) - 13;
            const angle = (360 / rays) * (particle - 1) + jitter;
            const dist = 88 + (particle % 4) * 22;
            const burstDelay = (burst - 1) * 0.42;
            const delay = burstDelay + (particle - 1) * 0.022;
            const duration = 1.18 + (burst + particle) % 3 * 0.1;
            const colors = ['#ffd700', '#ff8fab', '#7dd3fc', '#fde047', '#fb923c', '#c4b5fd', '#f0abfc', '#fff'];
            const color = colors[(burst + particle) % colors.length];
            return {
                '--fw-angle': `${angle}deg`,
                '--fw-dist': `${dist}px`,
                animationDelay: `${delay}s`,
                animationDuration: `${duration}s`,
                backgroundColor: color,
                boxShadow: `0 0 14px 4px ${color}`,
            };
        },

        resetForm() {
            this.errors = {};
            this.submitting = false;
            this.syncFormSectionSuccessClass(false);
            this.submitted = false;
            this.instantPrize = null;
            this.weight = 1;
            this.resultMessage = '';
            this.form = {
                firstName: '',
                lastName: '',
                entrantEmail: '',
                entrantPhone: '',
                spendAmount: '',
                store: '',
                terms: false,
            };
        },

        validate() {
            const errors = {};

            if (!this.form.firstName.trim()) {
                errors.firstName = 'First name is required.';
            }
            if (!this.form.lastName.trim()) {
                errors.lastName = 'Last name is required.';
            }

            const email = this.form.entrantEmail.trim();
            if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                errors.entrantEmail = 'A valid email is required.';
            }

            if (!this.form.entrantPhone.trim()) {
                errors.entrantPhone = 'Phone number is required.';
            }

            if (!this.form.spendAmount || this.form.spendAmount <= 0) {
                errors.spendAmount = 'Amount must be greater than zero.';
            }

            if (!this.form.store) {
                errors.store = 'Please select a store.';
            }

            if (!this.form.terms) {
                errors.terms = 'You must agree to the terms.';
            }

            this.errors = errors;
            return Object.keys(errors).length === 0;
        },

        async submitForm() {
            if (!this.validate()) return;

            this.submitting = true;
            this.errors = {};

            const fullName = `${this.form.firstName.trim()} ${this.form.lastName.trim()}`;

            const formData = new FormData();
            formData.append(this.csrfName, this.csrfToken);
            formData.append('entrantName', fullName);
            formData.append('entrantEmail', this.form.entrantEmail.trim());
            formData.append('entrantPhone', this.form.entrantPhone.trim());
            formData.append('spendAmount', this.form.spendAmount);
            formData.append('store', this.form.store);

            try {
                const response = await fetch('/actions/scanwin/public/submit-entry', {
                    method: 'POST',
                    body: formData,
                    credentials: 'same-origin',
                    headers: {
                        'Accept': 'application/json',
                        'X-CSRF-Token': this.csrfToken,
                    },
                });

                const data = await response.json();

                if (data.success) {
                    this.submitted = true;
                    this.resultMessage = data.message;
                    this.instantPrize = data.instantPrize || null;
                    this.weight = data.weight || 1;
                } else {
                    this.errors = data.errors || { form: 'Something went wrong. Please try again.' };
                }
            } catch (err) {
                this.errors = { form: 'Something went wrong. Please try again.' };
            } finally {
                this.submitting = false;
            }
        },
    },
};
</script>

<style scoped>
.scan-win-form {
    max-width: 100%;
}

.form-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
}

@media (max-width: 640px) {
    .form-grid {
        grid-template-columns: 1fr;
    }
}

.form-field input,
.form-field select {
    width: 100%;
    padding: 0.875rem 1.5rem;
    border: 1px solid #707070;
    border-radius: 4px;
    font-size: 1rem;
    background: #fff;
    outline: none;
    -webkit-appearance: none;
    color: #000;
}

.form-field input::placeholder {
    color: #4F706F;
}

.form-field select {
    color: #4F706F;
    background-image: url('../dist/images/icons/select.svg');
    background-repeat: no-repeat;
    background-position: calc(100% - 1.25rem) center;
    appearance: none;
}

.form-field select option:not(:first-child) {
    color: #000;
}

.form-field input:focus,
.form-field select:focus {
    border-color: #F6BE89;
    box-shadow: 0 0 0 3px rgba(246, 190, 137, 0.25);
}

.form-field.has-error input,
.form-field.has-error select {
    border-color: #e53e3e;
}

.error-msg {
    display: block;
    color: #e53e3e;
    font-size: 0.8rem;
    margin-top: 0.2rem;
    padding-left: 0.25rem;
}

.form-error {
    display: block;
    margin-bottom: 0.75rem;
    text-align: center;
}

.proof-notice {
    background: #6875b2;
    border-radius: 4px;
    padding: 0.625rem 1.25rem;
    font-size: 0.875rem;
    margin-top: 0.5rem;
    margin-bottom: 1rem;
    border: 3px solid #fff;
    color: #fff;
}

.terms-row {
    margin-bottom: 1rem;
}

.terms-label {
    display: flex;
    align-items: flex-start;
    gap: 0.875rem;
    cursor: pointer;
}

.terms-label input[type="checkbox"] {
    width: 1.5rem;
    height: 1.5rem;
    min-width: 1.5rem;
    border: 2px solid #112E29;
    border-radius: 3px;
    background: transparent;
    -webkit-appearance: none;
    appearance: none;
    cursor: pointer;
    display: grid;
    place-content: center;
    margin-top: 0.125rem;
}

.terms-label input[type="checkbox"]::before {
    content: "";
    width: 0.65em;
    height: 0.65em;
    clip-path: polygon(14% 44%, 0 65%, 50% 100%, 100% 16%, 80% 0%, 43% 62%);
    transform: scale(0);
    transition: 120ms transform ease-in-out;
    background: #fff;
}

.terms-label input[type="checkbox"]:checked {
    background: #112E29;
}

.terms-label input[type="checkbox"]:checked::before {
    transform: scale(1.3);
}

.terms-label span {
    font-size: 0.9375rem;
    line-height: 1.4;
}

.terms-label a {
    text-decoration: underline;
    color: inherit;
}

.terms-label a:hover {
    text-decoration: none;
}

.terms-sub {
    padding-left: 2.375rem;
    font-size: 0.8125rem;
    color: #112E29;
    margin-top: 0.25rem;
    line-height: 1.4;
}

.terms-row.has-error .error-msg {
    padding-left: 2.375rem;
}

.submit-btn {
    width: 100%;
    background: #000;
    color: #fff;
    text-transform: uppercase;
    font-size: 1.125rem;
    height: 4rem;
    border: none;
    cursor: pointer;
    letter-spacing: 0.02em;
    transition: background-color 0.5s ease-in-out, transform 0.35s cubic-bezier(0.22, 1, 0.36, 1),
        box-shadow 0.35s ease;
}

.submit-btn:hover:not(:disabled) {
    background: #000;
    color: #fff;
    transform: translate3d(0, -3px, 0);
    box-shadow: 0 12px 24px rgba(36, 31, 32, 0.18);
}

.submit-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

/* Success screen (non-winner) */
.success-screen {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    padding: 0.5rem 0 1.5rem;
    overflow: hidden;
    animation: success-screen-in 0.55s cubic-bezier(0.22, 1, 0.36, 1) both;
}

.success-screen--winner {
    overflow: visible;
}

.success-panel--instant-win {
    overflow: visible;
}

@keyframes success-screen-in {
    from {
        opacity: 0;
        transform: translateY(12px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.success-panel {
    position: relative;
    z-index: 1;
    width: 100%;
    max-width: 32rem;
    margin: 0 auto;
    padding: 2.25rem 1.75rem 2rem;
    background: rgba(255, 255, 255, 0.94);
    border: 1px solid rgba(255, 255, 255, 0.85);
    border-radius: 14px;
    box-shadow:
        0 4px 6px rgba(36, 31, 32, 0.04),
        0 22px 48px rgba(104, 117, 178, 0.18);
    text-align: center;
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    overflow: hidden;
}

@media (min-width: 1024px) {
    .success-panel {
        padding: 2.75rem 2.5rem;
    }
}

.success-panel-body {
    position: relative;
    z-index: 1;
}

.success-confetti {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 0;
}

.confetti-piece {
    position: absolute;
    top: -10px;
    border-radius: 2px;
    animation: confetti-fall 2.5s ease-out forwards;
    opacity: 0;
}

@keyframes confetti-fall {
    0% {
        transform: translateY(0) rotate(0deg);
        opacity: 0.85;
    }
    100% {
        transform: translateY(260px) rotate(720deg);
        opacity: 0;
    }
}

/* Fireworks — instant winner only */
.success-fireworks {
    position: absolute;
    inset: 0;
    pointer-events: none;
    z-index: 0;
}

.firework-burst {
    position: absolute;
    width: 0;
    height: 0;
}

.firework-particle {
    position: absolute;
    left: 0;
    top: 0;
    width: 11px;
    height: 11px;
    border-radius: 50%;
    transform: translate(-50%, -50%) rotate(var(--fw-angle)) translateY(0) scale(1);
    opacity: 0;
    animation-name: firework-ray;
    animation-timing-function: cubic-bezier(0.06, 0.75, 0.15, 1);
    animation-iteration-count: 1;
    animation-fill-mode: forwards;
}

@keyframes firework-ray {
    0% {
        transform: translate(-50%, -50%) rotate(var(--fw-angle)) translateY(0) scale(1);
        opacity: 0;
    }
    8% {
        opacity: 1;
    }
    42% {
        opacity: 1;
        transform: translate(-50%, -50%) rotate(var(--fw-angle)) translateY(calc(var(--fw-dist) * 0.55)) scale(0.92);
    }
    100% {
        transform: translate(-50%, -50%) rotate(var(--fw-angle)) translateY(var(--fw-dist)) scale(0.35);
        opacity: 0;
    }
}

.success-icon-wrap {
    margin-bottom: 1.35rem;
    animation: success-reveal 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.08) 0.08s both;
}

.success-icon {
    width: 4.5rem;
    height: 4.5rem;
    border-radius: 50%;
    background: linear-gradient(145deg, #34c889 0%, #2f9d6a 100%);
    box-shadow: 0 10px 28px rgba(47, 157, 106, 0.35);
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto;
    animation: success-icon-pop 0.55s cubic-bezier(0.34, 1.45, 0.64, 1) 0.15s both;
}

.success-icon--winner {
    background: #ffd700;
    box-shadow: 0 10px 28px rgba(230, 184, 0, 0.45);
}

.success-icon-svg {
    width: 2.25rem;
    height: 2.25rem;
}

@keyframes success-icon-pop {
    from {
        transform: scale(0.5);
        opacity: 0;
    }
    to {
        transform: scale(1);
        opacity: 1;
    }
}

@keyframes success-reveal {
    from {
        opacity: 0;
        transform: translateY(8px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.success-heading {
    font-family: 'DM Serif Display', serif;
    font-size: clamp(1.625rem, 4.2vw, 2.375rem);
    font-weight: 400;
    text-transform: uppercase;
    letter-spacing: -0.02em;
    line-height: 1.1;
    color: #111827;
    margin: 0 0 1rem;
    animation: success-reveal 0.55s cubic-bezier(0.22, 1, 0.36, 1) 0.22s both;
}

.success-multiplier {
    font-size: 1rem;
    line-height: 1.55;
    color: #241f20;
    margin: 0 auto 1rem;
    max-width: 28rem;
    animation: success-reveal 0.55s cubic-bezier(0.22, 1, 0.36, 1) 0.32s both;
}

.success-multiplier strong {
    font-weight: 700;
    color: #112e29;
}

.success-multiplier--prize {
    font-size: clamp(1.0625rem, 3.2vw, 1.3125rem);
    line-height: 1.5;
    max-width: 30rem;
}

.success-multiplier--prize strong {
    font-weight: 700;
    color: #1a1a1a;
}

.success-note {
    font-size: 0.875rem;
    line-height: 1.55;
    color: #4a4a4a;
    margin: 0 auto;
    max-width: 26rem;
    padding-top: 1rem;
    border-top: 1px solid rgba(112, 112, 112, 0.2);
    animation: success-reveal 0.55s cubic-bezier(0.22, 1, 0.36, 1) 0.4s both;
}

.success-reset-btn {
    margin-top: 1.5rem;
    animation: success-reveal 0.55s cubic-bezier(0.22, 1, 0.36, 1) 0.48s both;
}

@media (max-width: 1023px) {
    .success-screen {
        animation: none;
    }
    .success-confetti,
    .success-fireworks {
        display: none;
    }
    .success-icon-wrap,
    .success-icon,
    .success-heading,
    .success-multiplier,
    .success-note,
    .success-reset-btn {
        animation: none !important;
        opacity: 1;
        transform: none;
    }
}
</style>
